import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Constante } from '../../constante';
import { ResponseApi } from '../models/response-api';
import { TransaccionApi } from '../models/api/pedido-api';
import { AppUtil } from '../utility/app-util';

export interface RolApi {
    uuid: string;
    codigo: string;
    nombre: string;
    descripcion: string | null;
    sistema: boolean;
}

export interface MenuApi {
    uuid: string;
    codigo: string;
    nombre: string;
    ruta: string;
    icono: string | null;
    asignado: boolean;
}

@Injectable({ providedIn: 'root' })
export class RolService {
    private http = inject(HttpClient);
    private url = AppUtil.addTrailingSlash(environment.URL_API_PEDIDOS) + 'api/rol/';

    listRoles(): Observable<RolApi[]> {
        return this.http.get<ResponseApi<RolApi[]>>(this.url + 'list-roles').pipe(
            map((res) => (res.success === Constante.REQUEST_STATUS_OK && Array.isArray(res.data) ? res.data : []))
        );
    }

    createRol(body: { codigo: string; nombre: string; descripcion?: string | null }): Observable<TransaccionApi> {
        return this.http.post<ResponseApi<TransaccionApi>>(this.url + 'create-rol', body).pipe(
            map((res) => this.requireData(res))
        );
    }

    updateRol(uuid: string, body: { nombre: string; descripcion?: string | null }): Observable<TransaccionApi> {
        return this.http.put<ResponseApi<TransaccionApi>>(this.url + 'update-rol/' + uuid, body).pipe(
            map((res) => this.requireData(res))
        );
    }

    deleteRol(uuid: string): Observable<TransaccionApi> {
        return this.http.delete<ResponseApi<TransaccionApi>>(this.url + 'delete-rol/' + uuid).pipe(
            map((res) => this.requireData(res))
        );
    }

    listMenus(): Observable<MenuApi[]> {
        return this.http.get<ResponseApi<MenuApi[]>>(this.url + 'list-menus').pipe(
            map((res) => (res.success === Constante.REQUEST_STATUS_OK && Array.isArray(res.data) ? res.data : []))
        );
    }

    rolMenus(rolUuid: string): Observable<MenuApi[]> {
        return this.http.get<ResponseApi<MenuApi[]>>(this.url + 'rol-menus/' + rolUuid).pipe(
            map((res) => {
                if (res.success !== Constante.REQUEST_STATUS_OK || !Array.isArray(res.data)) {
                    throw new Error(this.readError(res));
                }
                return res.data;
            })
        );
    }

    assignMenu(rolUuid: string, menuUuid: string): Observable<TransaccionApi> {
        return this.http.post<ResponseApi<TransaccionApi>>(this.url + 'assign-menu/' + rolUuid, { menuUuid }).pipe(
            map((res) => this.requireData(res))
        );
    }

    removeMenu(rolUuid: string, menuUuid: string): Observable<TransaccionApi> {
        return this.http.delete<ResponseApi<TransaccionApi>>(this.url + 'remove-menu/' + rolUuid + '/' + menuUuid).pipe(
            map((res) => this.requireData(res))
        );
    }

    private requireData(res: ResponseApi<TransaccionApi>): TransaccionApi {
        if (res.success !== Constante.REQUEST_STATUS_OK || !res.data) {
            throw new Error(this.readError(res));
        }
        return res.data;
    }

    private readError(res: ResponseApi<unknown>): string {
        const errors = res.errors as { message?: string }[] | null;
        return errors?.[0]?.message || res.message || 'Ocurrió un error inesperado.';
    }
}
